import { Button, SwitchThemeContext, useViewport } from '@tryhackme/thm-ui-components';
import { useContext } from 'react';
import { BREAKPOINTS } from 'src/common/constants/breakpoints';
import { useIsDarkMode } from 'src/common/hooks/use-is-dark-mode';
import { useStepper } from 'src/common/hooks/use-stepper-modal';
import type { UserData } from 'src/common/interfaces/user';
import { useReprofilingState } from '../../hooks/use-reprofiling-state';
import { StyledBottomRow, StyledButtonRow, StyledProgressBar, StyledStepperModal } from './reprofiling-modal.styles';
import { ReprofilingStart } from './steps/reprofiling-start';
import { ReprofilingTechnicalAbility } from './steps/reprofiling-technical-ability';
export interface ReprofilingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserData['user'] | undefined;
}
interface StepData {
  name: string;
  value?: string;
  cta: string;
}
const CtaButton = (currentStep: number, stepsData: StepData[], onNext: () => void) => {
  return <Button onClick={onNext} data-sentry-element="Button" data-sentry-component="CtaButton" data-sentry-source-file="reprofiling-modal.tsx">{stepsData[currentStep]?.cta}</Button>;
};
const ReprofilingFooter = ({
  ammountOfSteps,
  currentStep,
  onNext,
  onBack,
  stepsData,
  isDark
}: {
  ammountOfSteps: number;
  currentStep: number;
  onNext: () => void;
  onBack: () => void;
  stepsData: StepData[];
  isDark: boolean;
}) => {
  return <StyledBottomRow data-sentry-element="StyledBottomRow" data-sentry-component="ReprofilingFooter" data-sentry-source-file="reprofiling-modal.tsx">
      <StyledProgressBar ammountOfSteps={ammountOfSteps} currentStep={currentStep + 1} $isDarkMode={isDark} data-sentry-element="StyledProgressBar" data-sentry-source-file="reprofiling-modal.tsx" />
      <StyledButtonRow data-sentry-element="StyledButtonRow" data-sentry-source-file="reprofiling-modal.tsx">
        {currentStep > 0 && <Button color="secondary" variant="outlined" onClick={onBack}>
            Back
          </Button>}
        {CtaButton(currentStep, stepsData, onNext)}
      </StyledButtonRow>
    </StyledBottomRow>;
};
export const ReprofilingModal: React.FC<ReprofilingModalProps> = ({
  open,
  onOpenChange,
  user
}) => {
  const {
    width
  } = useViewport();
  const isMobile = width < BREAKPOINTS.DESKTOP;
  const {
    theme
  } = useContext(SwitchThemeContext);
  const isDark = useIsDarkMode();
  const {
    getStepper
  } = useStepper();
  const {
    suggestion,
    selection,
    onSelectionChange
  } = useReprofilingState(user?.demographics?.experience);
  if (!user) return null;
  const stepsData: StepData[] = [{
    name: 'Name',
    value: user.username,
    cta: 'Adjust!'
  }, {
    name: 'Level',
    cta: "That's me!"
  }];
  const ammountOfSteps = stepsData.length;
  const {
    goToStep,
    currentStep
  } = getStepper('reprofiling-modal', ammountOfSteps, 0);
  const handleShowMore = () => {
    goToStep(currentStep + 1);
  };
  const reprofilingFooter = <ReprofilingFooter ammountOfSteps={ammountOfSteps} currentStep={currentStep} onNext={handleShowMore} onBack={() => goToStep(currentStep - 1)} stepsData={stepsData} isDark={isDark} />;
  return <StyledStepperModal open={open} onOpenChange={onOpenChange} id="reprofiling-modal" padding="0" overflowVisible $isMobile={isMobile} {...isMobile ? {
    maxHeight: 'auto'
  } : {
    minHeight: '63rem'
  }} steps={[{
    id: 'start',
    component: <ReprofilingStart name={user?.username} theme={theme} footer={reprofilingFooter} />,
    stepControllers: null
  }, {
    id: 'level',
    component: <ReprofilingTechnicalAbility footer={reprofilingFooter} ourSuggestion={suggestion} selectedExperience={selection} onSelectedExperienceChange={onSelectionChange} />,
    stepControllers: null
  }]} data-sentry-element="StyledStepperModal" data-sentry-component="ReprofilingModal" data-sentry-source-file="reprofiling-modal.tsx" />;
};